import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const CustomPagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange }) => {
  const { t } = useTranslation();

  // Track screen under 430px
  const [isSmallScreen, setIsSmallScreen] = useState(() => window.innerWidth < 420);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 420);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleClick = (page: number, e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const renderSmallScreenPages = () => {
    const items = [];

    // Always show page 1
    items.push(
      <li key={1} className={`paginate_button page-item ${currentPage === 1 ? 'active' : ''}`}>
        <a href="#" onClick={e => handleClick(1, e)} className="page-link">
          1
        </a>
      </li>,
    );

    // Show page 2 (if exists)
    if (totalPages >= 2) {
      items.push(
        <li key={2} className={`paginate_button page-item ${currentPage === 2 ? 'active' : ''}`}>
          <a href="#" onClick={e => handleClick(2, e)} className="page-link">
            2
          </a>
        </li>,
      );
    }

    // Show page 3 (if exists)
    if (totalPages >= 3) {
      items.push(
        <li key={3} className={`paginate_button page-item ${currentPage === 3 ? 'active' : ''}`}>
          <a href="#" onClick={e => handleClick(3, e)} className="page-link">
            3
          </a>
        </li>,
      );
    }

    // If totalPages > 3 and we are not already at the last page, show ellipses + last
    if (totalPages > 3) {
      // Only show ellipses if last page is > 3
      if (totalPages > 3) {
        items.push(
          <li key="dots" className="paginate_button page-item disabled">
            <a href="#" className="page-link">
              ...
            </a>
          </li>,
        );
      }

      // Show the last page
      items.push(
        <li key={totalPages} className={`paginate_button page-item ${currentPage === totalPages ? 'active' : ''}`}>
          <a href="#" onClick={e => handleClick(totalPages, e)} className="page-link">
            {totalPages}
          </a>
        </li>,
      );
    }

    return items;
  };

  const renderFullPages = () => {
    // Your original logic unchanged
    const pageNumbers = [];
    const maxButtons = 7; // or whatever you used

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(
          <li key={i} className={`paginate_button page-item ${i === currentPage ? 'active' : ''}`}>
            <a href="#" onClick={e => handleClick(i, e)} className="page-link">
              {i}
            </a>
          </li>,
        );
      }
    } else {
      let startPage = Math.max(2, currentPage - 2);
      let endPage = Math.min(totalPages - 1, currentPage + 2);

      if (currentPage <= 4) {
        startPage = 2;
        endPage = 5;
      } else if (currentPage >= totalPages - 3) {
        startPage = totalPages - 4;
        endPage = totalPages - 1;
      }

      pageNumbers.push(
        <li key={1} className={`paginate_button page-item ${currentPage === 1 ? 'active' : ''}`}>
          <a href="#" onClick={e => handleClick(1, e)} className="page-link">
            1
          </a>
        </li>,
      );

      if (startPage > 2) {
        pageNumbers.push(
          <li key="start-ellipsis" className="paginate_button page-item disabled">
            <a href="#" className="page-link">
              ...
            </a>
          </li>,
        );
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(
          <li key={i} className={`paginate_button page-item ${i === currentPage ? 'active' : ''}`}>
            <a href="#" onClick={e => handleClick(i, e)} className="page-link">
              {i}
            </a>
          </li>,
        );
      }

      if (endPage < totalPages - 1) {
        pageNumbers.push(
          <li key="end-ellipsis" className="paginate_button page-item disabled">
            <a href="#" className="page-link">
              ...
            </a>
          </li>,
        );
      }

      pageNumbers.push(
        <li key={totalPages} className={`paginate_button page-item ${currentPage === totalPages ? 'active' : ''}`}>
          <a href="#" onClick={e => handleClick(totalPages, e)} className="page-link">
            {totalPages}
          </a>
        </li>,
      );
    }

    return pageNumbers;
  };

  return (
    <div className="row mt-3">
      <div className="col-sm-12 col-md-5" />
      <div className="col-sm-12 col-md-7">
        <ul className="pagination d-flex justify-content-end">
          <li className={`paginate_button page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
            <a href="#" onClick={e => handleClick(currentPage - 1, e)} className="page-link">
              {t('PaginationButtons.previous')}
            </a>
          </li>

          {/* Use the small-screen or full version */}
          {isSmallScreen ? renderSmallScreenPages() : renderFullPages()}

          <li className={`paginate_button page-item next ${currentPage === totalPages ? 'disabled' : ''}`}>
            <a href="#" onClick={e => handleClick(currentPage + 1, e)} className="page-link">
              {t('PaginationButtons.next')}
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CustomPagination;
